import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { memo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { UnreadBadge } from '@/components/ui/UnreadBadge';
import { useLongPress } from '@/lib/useLongPress';

/**
 * A single direct-message row. Extracted into its own component so it can own a
 * `useLongPress` hook (touch long-press → the same folder-membership context
 * menu desktop opens on right-click) — hooks can't live inside the list's `map`
 * callback. Mouse keeps the native right-click; touch gets the long-press
 * fallback. Purely presentational otherwise; the caller computes all display
 * values.
 *
 * Memoized: the parent (HeroDmList) subscribes to the whole `messagesByChannel`
 * / `typingByChannel` maps, so a message or typing event in ANY conversation
 * re-renders the list. The callbacks are keyed by `channelId` so the parent can
 * pass referentially-stable handlers; with that + primitive display props, only
 * the row whose values actually changed re-renders, not every row.
 */
function DmRowImpl(props: {
  channelId: string;
  label: string;
  avatarUrl: string | null | undefined;
  fallbackSeed: string;
  active: boolean;
  time: string;
  preview: string | null;
  someoneTyping: boolean;
  tags: string[] | undefined;
  unread: number;
  mentions: number;
  onSelect: (channelId: string) => void;
  onOpenMenu: (channelId: string, x: number, y: number) => void;
}) {
  const { channelId, label, active, time, preview, someoneTyping, tags, unread, mentions } = props;
  const { t } = useTranslation();
  const { onOpenMenu } = props;
  const longPress = useLongPress((x, y) => onOpenMenu(channelId, x, y));
  return (
    <button
      type="button"
      onClick={() => props.onSelect(channelId)}
      onContextMenu={(e) => {
        e.preventDefault();
        onOpenMenu(channelId, e.clientX, e.clientY);
      }}
      {...longPress}
      aria-current={active ? 'true' : undefined}
      className={
        'studio-dm relative flex w-full select-none items-start rounded-lg px-2 py-1.5 text-left transition-colors [-webkit-touch-callout:none] ' +
        (active ? 'text-foreground' : 'elev-hover')
      }
    >
      {active && (
        <motion.span
          layoutId="hero-active-dm"
          className="absolute inset-0 rounded-lg bg-accent/[0.12]"
          transition={{ type: 'spring', bounce: 0.2, duration: 0.5 }}
        />
      )}
      {/* Two columns: name+preview on the left, date+badge on the right, with the
          row top-aligned so the date starts on the name's line.

          The date used to sit INSIDE the left column, on the name's own line.
          That reads fine until the preview — which has no sibling to push
          against — spans the full width and its ellipsis lands directly under the
          timestamp: 27px of overlap in the real stylesheet, exactly the date's
          width. Reserving that width with padding would work until the date gets
          wider (a different locale, a different font); its own column can't be
          reached at any preview length. */}
      <span className="relative flex min-w-0 flex-1 items-start gap-2.5">
        <IdentityAvatar id={props.fallbackSeed} name={label} src={props.avatarUrl} size="md" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className={
              'min-w-0 truncate text-sm ' + (active ? 'text-foreground' : 'text-foreground/90')
            }
          >
            {label}
          </span>
          {someoneTyping ? (
            <span className="truncate text-xs text-success" role="status" aria-live="polite">
              {t('dm.typing')}
            </span>
          ) : (
            preview && <span className="truncate text-xs text-muted">{preview}</span>
          )}
          {tags && tags.length > 0 && (
            <span className="mt-0.5 flex flex-wrap gap-1">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded bg-primary/15 px-1.5 py-px text-[10px] font-medium text-primary"
                >
                  {tag}
                </span>
              ))}
            </span>
          )}
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          {time && <span className="text-[10px] tabular-nums text-muted">{time}</span>}
          <UnreadBadge unread={unread} mentions={mentions} />
        </span>
      </span>
    </button>
  );
}

export const DmRow = memo(DmRowImpl);
